import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { checkLoginId } from '../api/UserAuthApi';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`;

const Box = styled.div`
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 40px 20px 32px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.4;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const Input = styled.input<{mb?: number}>`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: ${({ mb }) => mb || 0}px;
  background: #fafafa;
`;

const EmailInput = styled(Input)`
  margin-bottom: 24px;
`;

const CheckButton = styled.button`
  padding: 12px 16px;
  background: #2a3eb1;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
`;

const Message = styled.div<{color: string}>`
  font-size: 0.95rem;
  margin-top: 4px;
  margin-bottom: 16px;
  color: ${({ color }) => color};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &.next {
    background: #000;
    color: #fff;
    &:disabled {
      background: #bdbdbd;
      cursor: not-allowed;
    }
  }

  &.cancel {
    background: #eee;
    color: #666;
  }
`;

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [idCheckMsg, setIdCheckMsg] = useState('');
  const [idCheckColor, setIdCheckColor] = useState('');

  const handleNext = () => {
    if (!loginId || !password || !passwordConfirm || !email) {
      alert('모든 항목을 입력해주세요.');
      return;
    }

    if (password !== passwordConfirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    navigate('/signup/agreement', { 
      state: { afterLogin:false, loginId, password, email }
    });
  };

  const handleCancel = () => {
    navigate('/login');
  };

  const handleCheckLoginId = async () => {
    if (!loginId) {
      setIdCheckMsg('아이디를 입력해주세요.');
      setIdCheckColor('#ff6600');
      return;
    }
    try {
      const isDuplicated = (await checkLoginId(loginId)).response === true;
      if (isDuplicated) {
        setIdCheckMsg('중복된아이디입니다. 다시입력해주세요.');
        setIdCheckColor('#ff6600');
      } else {
        setIdCheckMsg('사용가능한 아이디입니다.');
        setIdCheckColor('#2a3eb1');
      }
    } catch (e) {
      setIdCheckMsg('아이디 중복확인 중 오류가 발생했습니다.');
      setIdCheckColor('#ff6600');
    }
  };

  return (
    <Wrapper>
      <Box>
        <Title>회원가입</Title>
        <InputRow>
          <Input
            type="text"
            placeholder="아이디"
            value={loginId}
            onChange={e => setLoginId(e.target.value)}
            mb={0}
          />
          <CheckButton type="button" onClick={handleCheckLoginId}>중복확인</CheckButton>
        </InputRow>
        <Message color={idCheckColor}>{idCheckMsg || '\u00A0'}</Message>
        <Input
          type="password"
          placeholder="비밀번호"
          value={password}
          onChange={e => setPassword(e.target.value)}
          mb={16}
        />
        <Input
          type="password"
          placeholder="비밀번호 확인"
          value={passwordConfirm}
          onChange={e => setPasswordConfirm(e.target.value)}
          mb={16}
        />
        <EmailInput
          type="email"
          placeholder="이메일 입력"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <ButtonGroup>
          <Button 
            className="next"
            onClick={handleNext}
            disabled={!loginId || !password || !passwordConfirm || !email}
          >
            다음
          </Button>
          <Button 
            className="cancel"
            onClick={handleCancel}
          >
            취소
          </Button>
        </ButtonGroup>
      </Box>
    </Wrapper>
  );
};

export default SignupPage; 